import { useEffect, useState } from "react";
import { Ship, Anchor, Route as RouteIcon, TicketCheck, IndianRupee, Users, Plus, Trash2, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { inr, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [ports, setPorts] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    const [s, p, r, b] = await Promise.all([
      api.get("/admin/stats"), api.get("/ports"), api.get("/routes"), api.get("/admin/bookings"),
    ]);
    setStats(s.data); setPorts(p.data); setRoutes(r.data); setBookings(b.data);
    // schedules per route
    const all = [];
    for (const rt of r.data) {
      const sc = await api.get(`/search?from_port=${rt.from_port_id}&to_port=${rt.to_port_id}&date=2026-01-01`);
      sc.data.schedules.forEach((x) => all.push({ ...x, route_label: `${rt.from_port?.city} → ${rt.to_port?.city}` }));
    }
    setSchedules(all);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const statCards = stats ? [
    { icon: TicketCheck, label: "Total bookings", value: stats.total_bookings },
    { icon: IndianRupee, label: "Revenue", value: inr(stats.revenue) },
    { icon: RouteIcon, label: "Routes", value: stats.routes },
    { icon: Users, label: "Customers", value: stats.users },
  ] : [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <p className="overline text-accent mb-1">Control centre</p>
        <h1 className="font-display font-bold text-2xl text-primary tracking-tight mb-6">Admin dashboard</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.label} className="bg-white border border-border rounded-xl p-5" data-testid={`stat-${c.label}`}>
              <c.icon className="h-5 w-5 text-accent mb-3" strokeWidth={1.75} />
              <div className="font-display font-bold text-2xl text-primary">{c.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="schedules" data-testid="tab-schedules">Schedules</TabsTrigger>
            <TabsTrigger value="routes" data-testid="tab-routes">Routes</TabsTrigger>
            <TabsTrigger value="ports" data-testid="tab-ports">Ports</TabsTrigger>
          </TabsList>

          {/* Bookings */}
          <TabsContent value="bookings" className="mt-5">
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground">
                  <tr className="text-left"><Th>Ref</Th><Th>Route</Th><Th>Date</Th><Th>Class</Th><Th>Amount</Th><Th>Status</Th></tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-border" data-testid={`admin-booking-${b.booking_ref}`}>
                      <Td className="font-mono text-xs">{b.booking_ref}</Td>
                      <Td>{b.from_port?.city} → {b.to_port?.city}</Td>
                      <Td>{b.travel_date && format(new Date(b.travel_date), "dd MMM")}</Td>
                      <Td className="capitalize">{b.travel_class}</Td>
                      <Td>{inr(b.amount)}</Td>
                      <Td><Badge variant={b.status === "PAID" ? "default" : "secondary"}>{b.status}</Badge></Td>
                    </tr>
                  ))}
                  {bookings.length === 0 && <tr><Td colSpan={6} className="text-center text-muted-foreground py-8">No bookings yet</Td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Schedules */}
          <TabsContent value="schedules" className="mt-5">
            <div className="flex justify-end mb-4"><ScheduleDialog routes={routes} onDone={load} /></div>
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground">
                  <tr className="text-left"><Th>Ferry</Th><Th>Route</Th><Th>Depart</Th><Th>Arrive</Th><Th>Economy</Th><Th>Business</Th><Th></Th></tr>
                </thead>
                <tbody>
                  {schedules.map((s) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`admin-schedule-${s.id}`}>
                      <Td>{s.ferry_name}</Td><Td>{s.route_label}</Td><Td>{s.departure_time}</Td><Td>{s.arrival_time}</Td>
                      <Td>{inr(s.economy_price)}</Td><Td>{inr(s.business_price)}</Td>
                      <Td><DeleteBtn onClick={() => api.delete(`/admin/schedules/${s.id}`).then(load)} testid={`del-schedule-${s.id}`} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Routes */}
          <TabsContent value="routes" className="mt-5">
            <div className="flex justify-end mb-4"><RouteDialog ports={ports} onDone={load} /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              {routes.map((r) => (
                <div key={r.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between" data-testid={`admin-route-${r.id}`}>
                  <div className="flex items-center gap-2 font-medium text-primary">
                    {r.from_port?.city} <ArrowRight className="h-4 w-4 text-accent" /> {r.to_port?.city}
                    <span className="text-xs text-muted-foreground font-normal">· {r.distance_km} km</span>
                  </div>
                  <DeleteBtn onClick={() => api.delete(`/admin/routes/${r.id}`).then(load)} testid={`del-route-${r.id}`} />
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Ports */}
          <TabsContent value="ports" className="mt-5">
            <div className="flex justify-end mb-4"><PortDialog onDone={load} /></div>
            <div className="grid gap-3 sm:grid-cols-3">
              {ports.map((p) => (
                <div key={p.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between" data-testid={`admin-port-${p.id}`}>
                  <div className="flex items-center gap-2">
                    <Anchor className="h-4 w-4 text-accent" />
                    <div>
                      <div className="font-medium text-primary text-sm">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.city} · {p.code}</div>
                    </div>
                  </div>
                  <DeleteBtn onClick={() => api.delete(`/admin/ports/${p.id}`).then(load)} testid={`del-port-${p.id}`} />
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium overline">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>{children}</td>;

function DeleteBtn({ onClick, testid }) {
  return (
    <button onClick={async () => { try { await onClick(); toast.success("Deleted"); } catch { toast.error("Failed"); } }} className="text-muted-foreground hover:text-destructive" data-testid={testid}>
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

function PortDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", code: "", city: "" });
  const save = async () => {
    try { await api.post("/admin/ports", f); toast.success("Port added"); setOpen(false); setF({ name: "", code: "", city: "" }); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-accent hover:bg-accent/90 text-white" data-testid="add-port-btn"><Plus className="h-4 w-4 mr-1.5" /> Add port</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add port</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name" v={f.name} on={(v) => setF({ ...f, name: v })} testid="port-name-input" />
          <Field label="Code" v={f.code} on={(v) => setF({ ...f, code: v.toUpperCase() })} testid="port-code-input" />
          <Field label="City" v={f.city} on={(v) => setF({ ...f, city: v })} testid="port-city-input" />
        </div>
        <DialogFooter><Button className="bg-accent hover:bg-accent/90 text-white" onClick={save} data-testid="save-port-btn">Save port</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RouteDialog({ ports, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ from_port_id: "", to_port_id: "", distance_km: "", image_url: "" });
  const save = async () => {
    if (!f.from_port_id || !f.to_port_id) return toast.error("Select both ports");
    try {
      await api.post("/admin/routes", { ...f, distance_km: parseFloat(f.distance_km || 0) });
      toast.success("Route added"); setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-accent hover:bg-accent/90 text-white" data-testid="add-route-btn"><Plus className="h-4 w-4 mr-1.5" /> Add route</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add route</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <PortSelect label="From port" v={f.from_port_id} on={(v) => setF({ ...f, from_port_id: v })} ports={ports} testid="route-from-select" />
          <PortSelect label="To port" v={f.to_port_id} on={(v) => setF({ ...f, to_port_id: v })} ports={ports} testid="route-to-select" />
          <Field label="Distance (km)" v={f.distance_km} on={(v) => setF({ ...f, distance_km: v })} type="number" testid="route-distance-input" />
          <Field label="Image URL" v={f.image_url} on={(v) => setF({ ...f, image_url: v })} testid="route-image-input" />
        </div>
        <DialogFooter><Button className="bg-accent hover:bg-accent/90 text-white" onClick={save} data-testid="save-route-btn">Save route</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ routes, onDone }) {
  const [open, setOpen] = useState(false);
  const init = { route_id: "", ferry_name: "", departure_time: "", arrival_time: "", duration_min: "", economy_price: "", business_price: "", economy_seats: "150", business_seats: "30" };
  const [f, setF] = useState(init);
  const save = async () => {
    if (!f.route_id) return toast.error("Select a route");
    try {
      await api.post("/admin/schedules", {
        ...f, duration_min: parseInt(f.duration_min || 0), economy_price: parseFloat(f.economy_price || 0),
        business_price: parseFloat(f.business_price || 0), economy_seats: parseInt(f.economy_seats || 0), business_seats: parseInt(f.business_seats || 0),
      });
      toast.success("Schedule added"); setOpen(false); setF(init); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-accent hover:bg-accent/90 text-white" data-testid="add-schedule-btn"><Plus className="h-4 w-4 mr-1.5" /> Add schedule</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add schedule</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Route</Label>
            <Select value={f.route_id} onValueChange={(v) => setF({ ...f, route_id: v })}>
              <SelectTrigger data-testid="schedule-route-select"><SelectValue placeholder="Select route" /></SelectTrigger>
              <SelectContent>{routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.from_port?.city} → {r.to_port?.city}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Ferry name" v={f.ferry_name} on={(v) => setF({ ...f, ferry_name: v })} testid="schedule-ferry-input" />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Depart" v={f.departure_time} on={(v) => setF({ ...f, departure_time: v })} placeholder="07:00" testid="schedule-depart-input" />
            <Field label="Arrive" v={f.arrival_time} on={(v) => setF({ ...f, arrival_time: v })} placeholder="08:00" testid="schedule-arrive-input" />
            <Field label="Mins" v={f.duration_min} on={(v) => setF({ ...f, duration_min: v })} type="number" testid="schedule-duration-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Economy ₹" v={f.economy_price} on={(v) => setF({ ...f, economy_price: v })} type="number" testid="schedule-eco-price-input" />
            <Field label="Business ₹" v={f.business_price} on={(v) => setF({ ...f, business_price: v })} type="number" testid="schedule-biz-price-input" />
          </div>
        </div>
        <DialogFooter><Button className="bg-accent hover:bg-accent/90 text-white" onClick={save} data-testid="save-schedule-btn">Save schedule</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, on, type = "text", placeholder = "", testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

function PortSelect({ label, v, on, ports, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={v} onValueChange={on}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Select port" /></SelectTrigger>
        <SelectContent>{ports.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
